import React, { useMemo } from "react";
import Select, { components } from "react-select";
import PropTypes from "prop-types";

const CustomMultiSelect = ({
  labelText,
  placeholder,
  errorText,
  options = [],
  keyId = "id",
  keyName = "name",
  defaultValue = [],
  onSubmit,
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  className = "",
  maxVisibleTags = 3,
  ...props
}) => {
  const selectOptions = useMemo(() => {
    return options.map((option) => ({
      value: option[keyId],
      label: option[keyName],
      data: option,
    }));
  }, [options, keyId, keyName]);

  const selectDefaultValue = useMemo(() => {
    if (!defaultValue || defaultValue.length === 0) return [];

    return defaultValue.map((item) => ({
      value: item[keyId],
      label: item[keyName],
      data: item,
    }));
  }, [defaultValue, keyId, keyName]);

  const handleChange = (selectedOptions) => {
    if (!selectedOptions) {
      onSubmit([]);
      return;
    }

    const selectedItems = selectedOptions.map((option) => option.data);
    onSubmit(selectedItems);
  };

  const MultiValueContainer = ({ children, ...props }) => {
    const selectedValues = props.selectProps.value || [];
    const currentValue = props.data;
    const index = selectedValues.findIndex(val => val.value === currentValue.value);
    
    if (index < maxVisibleTags) {
      return (
        <components.MultiValueContainer {...props}>
          {children}
        </components.MultiValueContainer>
      );
    }
    
    if (index === maxVisibleTags) {
      const remainingCount = selectedValues.length - maxVisibleTags;
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: '#e0e7ff',
          borderRadius: '6px',
          padding: '2px 8px',
          margin: '2px',
          fontSize: '14px',
          color: '#3730a3'
        }}>
          <span>+{remainingCount} more</span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmit([]);
            }}
            style={{
              marginLeft: '6px',
              background: 'none',
              border: 'none',
              color: '#6366f1',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: '1',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#c7d2fe';
              e.target.style.color = '#4338ca';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6366f1';
            }}
          >
            ×
          </button>
        </div>
      );
    }
    
    return null;
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: errorText ? "#ef4444" : state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: errorText ? "#ef4444" : "#9ca3af",
      },
      minHeight: "40px",
      backgroundColor: isDisabled ? "#f9fafb" : "white",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e0e7ff",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#3730a3",
      fontSize: "14px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6366f1",
      ":hover": {
        backgroundColor: "#c7d2fe",
        color: "#4338ca",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
      color: state.isSelected ? "white" : "#374151",
      ":hover": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#eff6ff",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <div className={`custom-multiselect ${className}`}>
      {labelText && <label className="block text-sm font-medium text-gray-700 mb-2">{labelText}</label>}

      <Select
        isMulti
        options={selectOptions}
        value={selectDefaultValue}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isSearchable={isSearchable}
        closeMenuOnSelect={false}
        styles={customStyles}
        className="react-select-container"
        classNamePrefix="react-select"
        noOptionsMessage={() => "Нет доступных опций"}
        loadingMessage={() => "Загрузка..."}
        components={{
          MultiValueContainer,
        }}
        {...props}
      />

      {errorText && <div className="mt-1 text-sm text-red-600">{errorText}</div>}
    </div>
  );
};

CustomMultiSelect.propTypes = {
  labelText: PropTypes.string,
  placeholder: PropTypes.string,
  errorText: PropTypes.string,
  options: PropTypes.array,
  keyId: PropTypes.string,
  keyName: PropTypes.string,
  defaultValue: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  className: PropTypes.string,
  maxVisibleTags: PropTypes.number,
};

export default CustomMultiSelect;